import { AlertCircle, BarChart2, Mail, MousePointer } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import { Campaign } from '@/types/marketing'

interface CampaignReportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaign: Campaign | null
}

export const CampaignReportDialog = ({
  open,
  onOpenChange,
  campaign,
}: CampaignReportDialogProps) => {
  if (!campaign || !campaign.stats) return null

  const { sent, opened, clicked, bounced } = campaign.stats
  const openRate = ((opened / sent) * 100).toFixed(1)
  const clickRate = ((clicked / opened) * 100).toFixed(1)
  const bounceRate = ((bounced / sent) * 100).toFixed(1)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart2 className="w-5 h-5" />
            Campaign Report: {campaign.name}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Sent
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-2xl font-bold">{sent.toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <MousePointer className="w-4 h-4" /> Opened
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-2xl font-bold">{opened.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">{openRate}% rate</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <MousePointer className="w-4 h-4" /> Clicked
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-2xl font-bold">{clicked.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">{clickRate}% rate</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Bounced
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-2xl font-bold">{bounced.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">{bounceRate}% rate</p>
              </CardContent>
            </Card>
          </div>

          <div className="border rounded-lg p-4 bg-muted/50">
            <h4 className="font-semibold mb-2">Campaign Details</h4>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <div className="text-muted-foreground">Subject Line:</div>
              <div>{campaign.subject}</div>
              <div className="text-muted-foreground">Audience:</div>
              <div>{campaign.audience}</div>
              <div className="text-muted-foreground">Sent Date:</div>
              <div>{campaign.sentAt?.toLocaleString()}</div>
              <div className="text-muted-foreground">Type:</div>
              <div className="capitalize">{campaign.type}</div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
